import { Entity } from '@api';
import { InsightIconsRoot } from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '@terminal/components/insights-tooltip/InsightsTooltip';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import insightIconStyles from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightIcon.module.scss';
import exploreTableStyles from '@terminal/views/explore/components/explore-table/ExploreTable.module.scss';
import { isToday } from 'date-fns';
import React, { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { InsightsWithDirection } from '../../explore-table-utils/explore-table-utils';
import { Count } from '../truncate-single-icons/count/Count';

const HORIZONTAL_CHART_COMBINE_SIZE = { width: '42px', height: '28px' };
export const HORIZONTAL_TABLE_COMBINE_SIZE = { width: '36px', height: '20px' };

export interface TruncateCombinedIconsProps {
  displayedInsights: InsightsWithDirection[][];
  entity?: Entity;
  shouldTruncate: boolean;
  insightsCount?: number;
  handleSelect?: (insightId: string, entity: Entity) => void;
  variant?: 'chart' | 'assets-table';
  source?: PosthogTrackingSource;
}

export const TruncateCombinedIcons = ({
  displayedInsights,
  insightsCount = 0,
  entity,
  shouldTruncate,
  handleSelect,
  variant = 'chart',
  source,
}: TruncateCombinedIconsProps) => {
  const { t } = useTranslation(['insights']);
  const size =
    variant === 'chart'
      ? HORIZONTAL_CHART_COMBINE_SIZE
      : HORIZONTAL_TABLE_COMBINE_SIZE;
  return (
    <span className={exploreTableStyles.insightsWrapper}>
      {displayedInsights.map((combine, index) => {
        if (!combine.length) {
          return null;
        }

        const {
          createdAt,
          direction,
          matched,
          horizon,
          episodesCount,
          median,
          stars,
          name,
          insightId,
        } = combine[0];

        const episodeLabel = t('insights:insightCard.episode', {
          count: episodesCount,
        });

        return (
          <span
            className={exploreTableStyles.insightsWrapper}
            key={`${direction}-${index}`}
          >
            <InsightsTooltip
              key={`${direction}-${index}`}
              conditions={combine}
              direction={direction}
              customSize={size}
              isStarred={isToday(new Date(createdAt))}
              isCombined
              layout="horizontal"
              horizon={horizon}
              episodeLabel={episodeLabel}
              median={median}
              stars={stars}
              name={name}
              entityTag={entity?.tag}
            >
              <div className={insightIconStyles.scenarioIconWrapper}>
                <InsightIconsRoot
                  conditions={combine}
                  direction={direction}
                  customSize={size}
                  isStarred={isToday(new Date(createdAt))}
                  isMatched={matched}
                  isCombined
                  layout="horizontal"
                  isSparklesCondensed={variant === 'assets-table'}
                  onIconClick={(e: SyntheticEvent) => {
                    e.stopPropagation();
                    handleSelect && entity
                      ? handleSelect(insightId, entity)
                      : null;
                  }}
                  source={source}
                />
              </div>
            </InsightsTooltip>
          </span>
        );
      })}
      {shouldTruncate && (
        <Count count={insightsCount - displayedInsights.length} />
      )}
    </span>
  );
};
